import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from '@sm-dashboard/types';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import instanceAxios from '@/util/axios';
import { getShluchimByIdUrl } from '@/constants';
import { MissingFieldsMessage } from '@/components/common/MissingFieldsMessage';

const PERSONAL_PROFILE_PATH = '/system/shluchim/profile';

// const PERSONAL_PROFILE_REQUIRED_FIELDS: { key: keyof User; label: string }[] = [
//   { key: 'fullAddress', label: 'כתובת אישית' },
//   { key: 'mikoud', label: 'מיקוד' },
//   { key: 'entrance', label: 'כניסה' },
//   { key: 'apartmentNumber', label: 'מספר דירה' },
//   { key: 'bankAccountName', label: 'שם בעלי החשבון' },
//   { key: 'bankNum', label: 'בנק' },
//   { key: 'bankBranchNum', label: 'מספר סניף' },
//   { key: 'bankAccountNum', label: 'מספר חשבון' }
// ];
//
// const isFilled = (value: unknown) => {
//   const text = String(value ?? '').trim();
//   return text !== '' && text !== 'null';
// };

export const getMissingPersonalProfileFields = (profile: User | null | undefined) => {
  if (!profile) return [];
  return [];
  // return PERSONAL_PROFILE_REQUIRED_FIELDS.filter(field => !isFilled(profile[field.key])).map(field => field.label);
};

const normalizePath = (path: string) => {
  const normalized = path.endsWith('/') ? path.slice(0, -1).toLowerCase() : path.toLowerCase();
  return normalized || '/';
};

const loadOwnProfile = async () => {
  const { data } = await instanceAxios.get(getShluchimByIdUrl);
  return (data || null) as User | null;
};

export const useRequiredPersonalProfileGate = () => {
  const [profile, setProfile] = useState<User | null | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const isOnPersonalProfile = normalizePath(location.pathname) === PERSONAL_PROFILE_PATH;
  const wasOnPersonalProfile = useRef(isOnPersonalProfile);

  useEffect(() => {
    let cancelled = false;
    loadOwnProfile()
      .then(data => {
        if (!cancelled) setProfile(data);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const leftPersonalProfile = wasOnPersonalProfile.current && !isOnPersonalProfile;
    wasOnPersonalProfile.current = isOnPersonalProfile;
    if (!leftPersonalProfile) return;

    let cancelled = false;
    setProfile(undefined);
    loadOwnProfile()
      .then(data => {
        if (!cancelled) setProfile(data);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOnPersonalProfile]);

  const missingFields = useMemo(() => getMissingPersonalProfileFields(profile), [profile]);
  const isIncomplete = missingFields.length > 0;

  return {
    isIncomplete,
    showDialog: profile !== undefined && isIncomplete && !isOnPersonalProfile,
    missingFields,
    goToProfile: () => navigate(PERSONAL_PROFILE_PATH, { replace: true })
  };
};

type RequiredPersonalProfileDialogProps = {
  open: boolean;
  missingFields: string[];
  onContinue: () => void;
};

const RequiredPersonalProfileDialog = ({
  open,
  missingFields,
  onContinue
}: RequiredPersonalProfileDialogProps) => {
  return (
    <DynamicDialog
      open={open}
      title="חסרים פרטים אישיים"
      subtitle="כעת תועבר למילוי הפרטים החסרים. ללא השלמתם לא ניתן לגלוש בדשבורד."
      description="הפרטים החסרים:"
      handleOkClose={onContinue}
      handleNotOkClose={onContinue}
      cancelAction={false}
      primaryAction={{ label: 'המשך למילוי הפרטים החסרים', onClick: onContinue }}
    >
      <MissingFieldsMessage fields={missingFields} />
    </DynamicDialog>
  );
};

export default RequiredPersonalProfileDialog;
